import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, BriefcaseBusiness, Clock3, Coins, Heart, MapPin } from "lucide-react";
import { toast } from "sonner";
import { acceptGetNow, declineOffer, getOffer, listSavedOpportunityIds, toggleSavedOpportunity } from "@/lib/partner.functions";
import { JobLocationMap } from "@/components/booking/JobLocationMap";
import { StatusPill } from "@/components/StatusPill";
import { gbp, sc, ukDate, ukDateTime } from "@/lib/format";
import { CreditBreakdown, OfferCountdown } from "@/components/partner/CreditBreakdown";
import { LiveOfferPanel } from "@/components/partner/LiveOfferPanel";
import { PartnerCreditsBidForm } from "@/components/partner/PartnerCreditsBidForm";
import { PartnerOfferChatCard } from "@/components/partner/PartnerOfferChatCard";
import { StickyJobActionBar } from "@/components/partner/StickyJobActionBar";
import { QuoteNegotiation } from "@/components/offers/QuoteNegotiation";
import { useBookingRealtime } from "@/hooks/useBookingRealtime";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/partner/offers/$offerId")({
  head: () => ({
    meta: [
      { title: "Job offer — Shields" },
      { name: "description", content: "Review this job offer, bid or use Get Now." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OfferPage,
});

function OfferPage() {
  const { offerId } = useParams({ from: "/_authenticated/partner/offers/$offerId" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["offer", offerId],
    queryFn: () => getOffer({ data: { offerId } }),
    // Realtime is primary; this is only a resilience fallback.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const savedQuery = useQuery({
    queryKey: ["partner-saved-opportunities"],
    queryFn: () => listSavedOpportunityIds(),
  });
  const [busy, setBusy] = useState(false);
  const [confirmGetNow, setConfirmGetNow] = useState(false);
  const [wonBookingId, setWonBookingId] = useState<string | null>(null);
  const [creditShortfall, setCreditShortfall] = useState<number | null>(null);
  const [chosenDate, setChosenDate] = useState("");
  const [chosenWindow, setChosenWindow] = useState("");
  const [savingOpportunity, setSavingOpportunity] = useState(false);

  const isSaved = (savedQuery.data ?? []).includes(offerId);

  async function toggleSaved() {
    if (savingOpportunity) return;
    setSavingOpportunity(true);
    try {
      const result = await toggleSavedOpportunity({ data: { offerId } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not update saved jobs");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["partner-saved-opportunities"] });
      toast.success(result.saved ? "Job saved" : "Job removed from saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update saved jobs");
    } finally {
      setSavingOpportunity(false);
    }
  }

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["offer", offerId] }),
      queryClient.invalidateQueries({ queryKey: ["partner-offers"] }),
    ]);
  }

  useBookingRealtime(data?.offer.booking_id ?? null, () => {
    void refresh();
  });

  async function handleGetNow() {
    if (!data) return;
    const cost = data.liveGetNowCredits;
    setConfirmGetNow(false);
    if (!chosenDate || !chosenWindow) {
      toast.error("Choose one of the customer availability slots first.");
      return;
    }
    setBusy(true);
    try {
      const result = await acceptGetNow({ data: { offerId, scheduledDate: chosenDate, scheduledWindow: chosenWindow } });
      if (!result.ok) {
        const required=(result as {required?:number}).required ?? cost;
        if (result.error === "insufficient_credits") setCreditShortfall(required);
        toast.error(
          result.error === "insufficient_credits"
            ? `Not enough Shields Credits — this job needs ${sc(required)}.`
            : result.error === "already_assigned"
              ? "Another Partner secured the job first. No Credits were charged."
              : (result.error ?? "We couldn't claim this job. Your Credits have not been charged."),
        );
        await refresh();
        return;
      }
      toast.success(`Job secured — ${sc(result.credits_charged ?? cost)} charged by the server.`);
      setWonBookingId(result.booking_id!);
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "We couldn't claim this job. No success has been assumed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDecline() {
    setBusy(true);
    try {
      const result = await declineOffer({ data: { offerId } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not decline this offer");
        return;
      }
      toast.success("Offer declined — no Credits used");
      navigate({ to: "/partner" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not decline this offer");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">This offer could not be found.</p>
        <Link to="/partner" className="font-semibold text-primary underline">Back to workspace</Link>
      </div>
    );
  }

  const { offer, myBid, rivalBids, liveGetNowCredits, openingGetNowCredits, auctionEndsAt, urgency, highestCreditsBid, walletBalance } = data;
  const guideMidPence = offer.estimate_low_pence != null && offer.estimate_high_pence != null
    ? Math.round((offer.estimate_low_pence + offer.estimate_high_pence) / 2)
    : null;
  const open = ["sent", "viewed", "bid_submitted"].includes(offer.status) && new Date(offer.expires_at) > new Date();
  const memberGetNowCredits = data.repeatDiscountEligible ? Math.max(5, Math.round((liveGetNowCredits * 0.75) / 5) * 5) : liveGetNowCredits;

  return (
    <div className="partner-quote-ref-page">
      <main className="partner-quote-ref-main">
        <header className="partner-quote-ref-top">
          <Link to="/partner/offers" aria-label="Back to opportunities"><ArrowLeft /></Link>
          <button type="button" aria-label={isSaved ? "Remove saved job" : "Save job"} aria-pressed={isSaved} disabled={savingOpportunity} onClick={() => void toggleSaved()}><Heart /></button>
        </header>

        {data.media?.length ? <div className="partner-quote-ref-hero">
          <img src={data.media[0]?.url ?? ""} alt="Customer job" />
          {data.media.length>1&&<div className="partner-quote-ref-media-count">+{data.media.length-1} photos</div>}
        </div> : <div className="partner-quote-ref-hero is-empty"><BriefcaseBusiness/><span>Customer job details</span></div>}

        <section className="partner-quote-ref-summary">
          <div className="partner-quote-ref-title"><div><h1>{offer.service_name ?? "Job opportunity"}</h1><strong>{offer.estimate_low_pence!=null?gbp(offer.estimate_low_pence)+" – "+gbp(offer.estimate_high_pence??offer.estimate_low_pence):"Quote required"}</strong></div>{urgency==="urgent"&&<span>Urgent</span>}</div>
          <p className="partner-quote-ref-location"><MapPin/>{offer.outward_postcode || "Local area"} <small>· approximate area</small></p>
          <div className="partner-quote-ref-divider"/>
          <p className="partner-quote-ref-description">{offer.scope_summary || "Open the quote flow to discuss the customer's requirements."}</p>
          <div className="partner-quote-ref-facts">
            <p><BriefcaseBusiness/><span>{offer.service_name || "Trade service"}</span></p>
            <p><Clock3/><span>{urgency==="urgent"?"ASAP":offer.preferred_date?ukDate(offer.preferred_date):"Flexible date"}</span></p>
            {offer.preferred_window&&<p><Clock3/><span>{offer.preferred_window}</span></p>}
          </div>
        </section>

        {open&&<section id="price-agreement" className="partner-quote-ref-quote"><QuoteNegotiation bookingId={offer.booking_id} partnerId={offer.partner_id} role="partner" guidePence={guideMidPence}/></section>}

        <div className="partner-quote-ref-actions">
          {open ? (
            <a href="#price-agreement" className="partner-quote-ref-primary">Send quote</a>
          ) : (
            <span className="partner-quote-ref-primary" aria-disabled="true">Opportunity closed</span>
          )}
          <PartnerOfferChatCard bookingId={offer.booking_id}/>
          <button type="button" disabled={savingOpportunity || isSaved} onClick={() => void toggleSaved()} className="partner-quote-ref-save"><Heart/> {isSaved ? "Saved" : "Save Job"}</button>
        </div>

        {open&&data.priceAgreed&&<details className="partner-quote-ref-next"><summary>Price agreed — bidding &amp; Get Now</summary>
          {data.preferredRepeatCustomer&&<div className={"repeat-customer-discount "+(data.repeatDiscountEligible?"is-active":"is-locked")}><Heart/><span><strong>Repeat customer</strong><small>{data.repeatDiscountEligible?"25% off the Credits charged if you win or Get Now — your membership benefit is active.":"This customer asked to have you again. Upgrade to Business Pro or higher to unlock 25% off winning/claim Credits."}</small></span>{data.repeatDiscountEligible?<b>25% OFF</b>:<Link to="/plans">Unlock</Link>}</div>}
          {(offer.preferred_date||data.alternativeDate)&&<div className="partner-quote-ref-slots"><p>Choose a customer-offered date and time:</p>{offer.preferred_date&&<button onClick={()=>{setChosenDate(offer.preferred_date!);setChosenWindow(offer.preferred_window||"Flexible")}}>{ukDate(offer.preferred_date)} · {offer.preferred_window||"Flexible"}</button>}{data.alternativeDate&&<button onClick={()=>{setChosenDate(data.alternativeDate!);setChosenWindow(data.alternativeWindow||"Flexible")}}>{ukDate(data.alternativeDate)} · {data.alternativeWindow||"Flexible"}</button>}</div>}
          {offer.get_now_credits!=null&&<LiveOfferPanel liveCredits={memberGetNowCredits} openingCredits={data.repeatDiscountEligible?Math.max(5,Math.round((openingGetNowCredits*0.75)/5)*5):openingGetNowCredits} auctionEndsAt={auctionEndsAt} urgency={urgency} rivalBids={rivalBids} walletBalance={walletBalance} busy={busy} onGetNow={()=>setConfirmGetNow(true)}/>}
          <PartnerCreditsBidForm offerId={offerId} existing={myBid} busy={busy} setBusy={setBusy} onDone={refresh} rivalBids={rivalBids} guideMidPence={data.agreedPricePence??guideMidPence} urgency={urgency} highestCreditsBid={highestCreditsBid} walletBalance={walletBalance} customerDates={[offer.preferred_date?{date:offer.preferred_date,window:offer.preferred_window||"Flexible"}:null,data.alternativeDate?{date:data.alternativeDate,window:data.alternativeWindow||"Flexible"}:null].filter(Boolean) as {date:string;window:string}[]} repeatCustomerDiscountPercent={data.repeatDiscountPercent}/>
        </details>}
      </main>
      <Dialog open={confirmGetNow} onOpenChange={setConfirmGetNow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Take this job now?</DialogTitle>
            <DialogDescription>
              Claim {chosenDate ? `${ukDate(chosenDate)} · ${chosenWindow}` : "this job"} for exactly {sc(memberGetNowCredits)}. Shields verifies the customer offered that slot and checks your diary before assignment. If another Partner wins first, you are not charged.
            </DialogDescription>
          </DialogHeader>
          <CreditBreakdown valuePence={data.agreedPricePence ?? guideMidPence ?? 0} rivalBids={rivalBids} total={memberGetNowCredits} heading={data.repeatDiscountEligible?"Member repeat-customer charge · 25% off":"Server-authoritative charge"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmGetNow(false)} className="min-h-11">Not yet</Button>
            <Button onClick={handleGetNow} disabled={busy || !chosenDate || !chosenWindow} className="btn-glow min-h-11">{busy ? "Securing job…" : "Confirm Get Now"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={creditShortfall !== null} onOpenChange={(openDialog) => !openDialog && setCreditShortfall(null)}>
        <DialogContent className="credit-shortfall-dialog">
          <DialogHeader>
            <span className="credit-shortfall-icon"><Coins aria-hidden /></span>
            <DialogTitle>Top up your Shields Credits</DialogTitle>
            <DialogDescription>This opportunity needs {sc(creditShortfall ?? 0)}. No Credits were charged.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditShortfall(null)} className="min-h-11">Not now</Button>
            <Button asChild className="min-h-11"><Link to="/partner/wallet">Buy Credits securely</Link></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={wonBookingId !== null} onOpenChange={() => {}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Job secured</DialogTitle>
            <DialogDescription>Your Credits charge is confirmed. Customer GBP payment is a separate protected-payment step and is not shown as paid until Stripe confirms it.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-2 text-sm">
            <li className="rounded-lg bg-muted px-4 py-3">1. Full customer job details are unlocked.</li>
            <li className="rounded-lg bg-muted px-4 py-3">2. Customer completes protected Stripe payment.</li>
            <li className="rounded-lg bg-muted px-4 py-3">3. Complete the work and submit evidence.</li>
            <li className="rounded-lg bg-muted px-4 py-3">4. Customer approval triggers protected payout release.</li>
          </ol>
          <DialogFooter>
            <Button className="min-h-11 w-full font-bold" onClick={() => navigate({ to: "/partner/jobs/$bookingId", params: { bookingId: wonBookingId! } })}>Open job &amp; payment status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {wonBookingId ? (
        <StickyJobActionBar label="Job secured" value="Credits charged successfully" note="Next: protected customer payment">
          <Link to="/partner/jobs/$bookingId" params={{ bookingId: wonBookingId }} className="inline-flex min-h-11 items-center justify-center border border-primary bg-primary px-4 text-sm font-extrabold text-primary-foreground">Open job</Link>
        </StickyJobActionBar>
      ) : open && data.priceAgreed ? (
        <StickyJobActionBar label="Choose how to win" value={`${sc(memberGetNowCredits)} Get Now`} note={`Normal bid costs 0 SC now · ${sc(walletBalance)} wallet`}>
          <a href="#credits-bid" className="inline-flex min-h-11 items-center justify-center border border-primary/50 bg-background px-4 text-sm font-extrabold text-primary">Place bid</a>
          <Button onClick={() => setConfirmGetNow(true)} disabled={busy || walletBalance < memberGetNowCredits} className="min-h-11 font-extrabold">Get Now</Button>
        </StickyJobActionBar>
      ) : open ? (
        <StickyJobActionBar label="Next step" value="Agree the GBP price" note="Credits stay locked until both sides agree">
          <a href="#price-agreement" className="inline-flex min-h-11 items-center justify-center border border-primary bg-primary px-4 text-sm font-extrabold text-primary-foreground">Open quote</a>
        </StickyJobActionBar>
      ) : null}
    </div>
  );
}